"use client";

/**
 * The client half of deploy detection: one module-level store, shared by the
 * banner and by all three error boundaries.
 *
 * ## The failure it is here for
 *
 * Jana is replaced in place on a single container. A tab open across a redeploy
 * keeps running the old client bundle, and its **server actions stop existing**
 * — the POST answers 404 with `x-nextjs-action-not-found: 1`, Next 16 throws an
 * `UnrecognizedActionError`, and with the §11 shape this repo uses (the action
 * passed straight to `<form action={…}>`, no `useActionState`) nothing catches
 * it, so it lands in `error.tsx` as an anonymous "something went wrong". The
 * merchant reads that as Jana losing their edit. It is instead a tab that needs
 * reloading, and that is a sentence worth saying.
 *
 * ## Two states, and they are not the same claim
 *
 * - **`stale`** — the poll found a different build. Nothing has failed yet;
 *   everything on screen still works until an action is fired. This is an
 *   invitation, and it is dismissible.
 * - **`blocked`** — an action really was refused. The user has already pressed
 *   Save and it did nothing. This is not dismissible, because dismissing it
 *   leaves them in a tab where every button is inert with no explanation.
 *
 * Keeping them apart is the point. Collapsing them means either nagging on the
 * first (a modal for something that has not broken) or under-reporting the
 * second (a corner toast for a page that cannot save).
 *
 * ## ⚠️ Nothing here ever reloads on its own
 *
 * The reload is always a button the user presses. An automatic one would throw
 * away a half-written product description or a checkout form the moment a deploy
 * landed — and the population most likely to be mid-edit is exactly the
 * population a redeploy interrupts. `blocked` is the strongest state and it
 * still only asks. Do not "improve" this into a timer.
 *
 * ## ⚠️ `servedBuild` is the bundle's identity, not the document's
 *
 * It is seeded once, from the value the root layout rendered, and never
 * reassigned — a client-side navigation must not refresh it, because the running
 * JS is still the old build even when the RSC payload came from the new server.
 * A module-level `let` is exactly right here: it is reset by the one event that
 * genuinely gives the tab a new bundle, which is a full page load.
 */

export interface AppUpdateState {
	/** A different build is live. Nothing has failed; the tab is just behind. */
	stale: boolean;
	/** A server action was refused because it no longer exists. Already broken. */
	blocked: boolean;
}

const IDLE: AppUpdateState = { stale: false, blocked: false };

/** Re-checked at most this often, however many wake-ups arrive. */
const CHECK_THROTTLE_MS = 30_000;
/** How long "Later" hides the non-urgent banner for. */
const SNOOZE_MS = 30 * 60_000;

const listeners = new Set<() => void>();

let snapshot: AppUpdateState = IDLE;
let servedBuild: string | null = null;
let liveBuild: string | null = null;
let blocked = false;
let snoozedUntil = 0;
let screenOwners = 0;
let lastCheck = 0;
let inFlight: Promise<void> | null = null;

/** Seed the identity of the bundle this tab is running. First call wins. */
export function setServedBuild(id: string): void {
	servedBuild ??= id;
	recompute();
}

export function subscribeToAppUpdate(fn: () => void): () => void {
	listeners.add(fn);
	return () => {
		listeners.delete(fn);
	};
}

export function getAppUpdateSnapshot(): AppUpdateState {
	return snapshot;
}

/**
 * The server render can never know either half — it has no poll result and no
 * refused action — so it is always `IDLE`, and it is a stable reference so
 * `useSyncExternalStore` does not loop.
 */
export function getAppUpdateServerSnapshot(): AppUpdateState {
	return IDLE;
}

/**
 * Ask the server which build it is. Throttled, and de-duplicated across
 * concurrent callers, because every wake-up path (interval, tab focus,
 * visibility, a refused action) can fire at once when a laptop lid opens.
 *
 * It never throws and never reports a failure: a version check that cannot reach
 * the server tells the user nothing they can act on, and the page they are on is
 * working fine.
 */
export async function checkForUpdate(options?: { force?: boolean }): Promise<void> {
	if (inFlight) return inFlight;
	if (!options?.force && Date.now() - lastCheck < CHECK_THROTTLE_MS) return;

	inFlight = (async () => {
		try {
			const res = await fetch("/api/version", {
				cache: "no-store",
				headers: { accept: "application/json" },
			});
			if (!res.ok) return;
			const body: unknown = await res.json();
			const build = (body as { build?: unknown } | null)?.build;
			if (typeof build === "string" && build) {
				liveBuild = build;
				recompute();
			}
		} catch {
			// Offline, or the container is mid-restart. Either way there is nothing
			// to say and nothing to change; the next wake-up asks again.
		} finally {
			lastCheck = Date.now();
			inFlight = null;
		}
	})();

	return inFlight;
}

/**
 * Called from an error boundary that has identified a refused server action.
 *
 * It does not wait for the version check to agree, and that is deliberate: the
 * 404 already IS the evidence — the action this bundle knows about is not on the
 * server — so the state is set first and the poll only fills in the build id
 * afterwards. Gating on the poll would leave a user whose network check fails
 * looking at the generic error screen for a failure we had already diagnosed.
 */
export function markUpdateBlocked(): void {
	blocked = true;
	snoozedUntil = 0;
	recompute();
	void checkForUpdate({ force: true });
}

/**
 * Claim the screen: an error boundary that is itself telling the user to reload
 * calls this while it is mounted, and the banner stays out of its way.
 *
 * Without it the two would both be on screen at once saying the same sentence —
 * a full-page "Jana was updated, reload" with a corner card under it repeating
 * it. A counter rather than a boolean because two nested boundaries can both be
 * mounted (the panel one inside the root one), and a boolean would be cleared by
 * whichever unmounted first.
 */
export function claimUpdateScreen(): () => void {
	screenOwners += 1;
	recompute();
	return () => {
		screenOwners -= 1;
		recompute();
	};
}

/** "Later" — hide the non-urgent banner. Has no effect once `blocked`. */
export function snoozeAppUpdate(): void {
	snoozedUntil = Date.now() + SNOOZE_MS;
	recompute();
}

/** The one place the page is thrown away. Always behind a button press. */
export function reloadForUpdate(): void {
	globalThis.location.reload();
}

function recompute(): void {
	const owned = screenOwners > 0;
	const stale =
		!owned &&
		servedBuild !== null &&
		liveBuild !== null &&
		liveBuild !== servedBuild &&
		Date.now() >= snoozedUntil;
	const blockedNow = !owned && blocked;

	if (stale === snapshot.stale && blockedNow === snapshot.blocked) return;

	// A new object only when something actually moved: `useSyncExternalStore`
	// compares snapshots by identity and re-renders every subscriber on a fresh
	// one, so recomputing into a new literal on every poll would re-render the
	// whole tree every 30 seconds for nothing.
	snapshot = { stale, blocked: blockedNow };
	for (const listener of listeners) listener();
}
